import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toInputDate, toInputDateTime } from '../../utils/dateFormat';
import './DateInput.css';

// 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm' -> local Date. `new Date('YYYY-MM-DD')`
// would be UTC midnight (the previous day west of UTC), so parse by hand.
function parseValue(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(String(value));
    if (m) return new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Drop-in replacement for <input type="date"> / <input type="datetime-local">
 * that always shows the date as "07 Oct 2026" (the native inputs follow the
 * browser locale, which on an en-US browser is mm/dd/yyyy). Typing
 * dd/mm/yyyy (or "07 Oct 2026") works too.
 *
 * Same contract as the native input, so callers don't change: `value` is
 * 'YYYY-MM-DD' (or 'YYYY-MM-DDTHH:mm' for datetime-local), `min`/`max` the
 * same, and `onChange` receives an event-like `{ target: { name, value, type } }`
 * with the value in that same format ('' when cleared).
 */
const DateInput = ({
    type = 'date',
    value,
    onChange,
    name,
    id,
    min,
    max,
    required,
    disabled,
    className = '',
    title,
    placeholder,
    autoFocus,
}) => {
    const withTime = type === 'datetime-local';

    const handleChange = (date) => {
        const next = date ? (withTime ? toInputDateTime(date) : toInputDate(date)) : '';
        onChange?.({ target: { name, value: next, type } });
    };

    return (
        <DatePicker
            selected={parseValue(value)}
            onChange={handleChange}
            // First format is shown; every format is accepted when typing.
            dateFormat={withTime
                ? ['dd MMM yyyy hh:mm aa', 'dd/MM/yyyy hh:mm aa']
                : ['dd MMM yyyy', 'dd/MM/yyyy']}
            placeholderText={placeholder || (withTime ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy')}
            showTimeSelect={withTime}
            timeIntervals={15}
            timeCaption="Time"
            minDate={parseValue(min)}
            maxDate={parseValue(max)}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            name={name}
            id={id}
            title={title}
            required={required}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete="off"
            className={`date-input ${className}`.trim()}
            wrapperClassName="date-input-wrapper"
            // Render the calendar outside the page so modals with
            // overflow:hidden don't clip it.
            portalId="date-input-portal"
            popperPlacement="bottom-start"
        />
    );
};

export default DateInput;
